import { useState } from "react";
import classNames from "classnames";

import type { TableDataType, ConfigType } from "../types";
import { formatMoney } from "../utils";
import { ensureValidUrlForWebsiteUrl } from "lib/utils";
import RankDisplay from "./rankDisplay/rankDisplay";

type RowProps = {
  entry: TableDataType;
  tableConfig: ConfigType;
  index: number;
  onTrackMixpanel?: (
    event: string,
    data: {
      [key: string]: string;
    }
  ) => Promise<void>;
};

const Row = ({ entry, tableConfig, index, onTrackMixpanel }: RowProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatMoneyValue = (
    value: string | number | [string | undefined, string | undefined]
  ): string => {
    // Handle tuple format [value, unit] or plain value
    if (Array.isArray(value)) {
      const [amount, unit] = value;
      if (amount && unit) return `${amount}${unit}`;
      if (amount) return String(amount);
      return "-";
    }

    if (!value) return "-";

    const formatted = formatMoney(value);
    if (!formatted) return String(value);
    const [num, suffix] = formatted;
    return `€${num}${suffix}`;
  };

  const handleClickOnRow = async () => {
    if (onTrackMixpanel) {
      await onTrackMixpanel("Ranking company info click", {
        Component: "Ranking Table",
        "Click text":
          (entry.company as string) || (entry.investorName as string) || "",
      });
    }
    setIsExpanded(!isExpanded);
  };
  // Extract key data for display
  const isCompany = "company" in entry || "companyAndUrl" in entry;
  const name = isCompany
    ? (entry.company as string) ||
      (Array.isArray(entry.companyAndUrl) ? entry.companyAndUrl[0] : "")
    : (entry.investorName as string) ||
      (Array.isArray(entry.investorNameAndUrl)
        ? entry.investorNameAndUrl[0]
        : "");
  const logo = isCompany ? entry.logoUrl : entry.investorLogo;
  const websiteUrl = isCompany
    ? (entry.website as string) ||
      (Array.isArray(entry.companyAndUrl) ? entry.companyAndUrl[1] : "")
    : (entry.investorUrl as string) ||
      (Array.isArray(entry.investorNameAndUrl)
        ? entry.investorNameAndUrl[1]
        : "");

  // Helper function to format cell data based on column type
  const formatCellData = (value: any, type: string): string => {
    if (!value) return "-";

    switch (type) {
      case "money":
        return formatMoneyValue(value);
      case "location":
        if (Array.isArray(value)) {
          const [city, country] = value;
          return city && country
            ? `${city}, ${country}`
            : country || city || "-";
        }
        return String(value);
      case "link":
        if (Array.isArray(value)) {
          return value[0] || "-";
        }
        return String(value);
      default:
        return String(value);
    }
  };
  const dataColumns = tableConfig.columns.filter(
    (column) =>
      column.type !== "fixedSizeImage" &&
      column.dataName !== "companyAndUrl" &&
      column.dataName !== "investorNameAndUrl" &&
      column.dataName !== "company" &&
      column.dataName !== "investorName" &&
      column.dataName !== "rank"
  );

  return (
    <button
      className={classNames(
        "flex w-full rounded-sm border border-gray-100 bg-[#FFFFFF] p-4 text-left",
        "focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-cerise"
      )}
      onClick={handleClickOnRow}
      aria-expanded={isExpanded ? "true" : "false"}
      aria-label={"Open details"}
      data-testid="table-row"
    >
      <div
        className={
          "flex w-full flex-col gap-[18px] md:flex-col lg:flex-row lg:flex-nowrap lg:justify-between"
        }
      >
        {/* Company/Investor info section */}
        <div
          className={
            "flex w-full max-w-[100%] flex-row flex-nowrap gap-[18px] lg:grow"
          }
        >
          <div className={"flex flex-col gap-[14px]"}>
            <div
              className={"font-bold text-[14px] leading-[130%] text-mono-20"}
            >
              Rank
            </div>
            <RankDisplay entry={entry} />
          </div>
          <div className={"flex grow flex-col gap-[14px]"}>
            <div
              className={"font-bold text-[14px] leading-[130%] text-mono-20"}
            >
              {isCompany ? "Company info" : "Investor"}
            </div>
            <div className={"flex items-start gap-[8px] leading-[130%]"}>
              {logo && websiteUrl ? (
                <a
                  href={ensureValidUrlForWebsiteUrl(websiteUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <img
                    className={"h-[50px] w-[50px] object-contain"}
                    src={logo as string}
                    alt={`${name} logo`}
                    loading="lazy"
                  />
                </a>
              ) : logo ? (
                <img
                  className={"h-[50px] w-[50px] object-contain"}
                  src={logo as string}
                  alt={`${name} logo`}
                  loading="lazy"
                />
              ) : null}
              <div className={"flex flex-col gap-[8px] text-[16px]"}>
                {websiteUrl ? (
                  <a
                    href={ensureValidUrlForWebsiteUrl(websiteUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={"font-bold"}
                    data-testid="company-name"
                  >
                    {name as string}
                  </a>
                ) : (
                  <h4 className={"font-bold"} data-testid="company-name">
                    {name as string}
                  </h4>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Right side - Data columns */}
        <div
          className={
            "flex w-full flex-row flex-wrap gap-[30px] text-[16px] leading-[130%] md:flex-nowrap md:gap-[18px] lg:ml-0 lg:flex-nowrap lg:justify-end lg:gap-[18px]"
          }
        >
          {dataColumns.map((column) => {
            const value = entry[column.dataName as string];
            const alwaysShowColumns = [
              "fundLaunchYear",
              "founded",
              "stage",
              "lastRoundSize",
              "totalFunding",
              "dealCount",
              "focus",
              "fundType",
              "latestFundSize",
              "unicornCount",
            ];
            const shouldShowColumn =
              value || alwaysShowColumns.includes(column.dataName as string);
            if (!shouldShowColumn) return null;
            return (
              <div
                key={column.dataName}
                className={`flex flex-[0_0_calc(50%_-_15px)] flex-col gap-[14px] text-left md:flex-1 lg:flex-1`}
              >
                <div
                  className={
                    "whitespace-nowrap font-bold text-[14px] leading-[130%] text-mono-20"
                  }
                >
                  {column.displayName || column.dataName}
                </div>
                <div className={"leading-[130%]"}>
                  <div data-testid={`${column.dataName}`}>
                    {value
                      ? formatCellData(value, column.type || "string")
                      : "-"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </button>
  );
};

export default Row;
